import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router'; // Router can be used to get current navigation state
import { CommonModule } from '@angular/common'; // For *ngFor, *ngIf
import { FormsModule } from '@angular/forms'; // For future form for creating repack
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmButton } from '@spartan-ng/helm/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { HlmIcon } from '@spartan-ng/helm/icon';
import { lucideArrowLeft, lucideClipboard, lucideCheck, lucideAlertTriangle } from '@ng-icons/lucide';
import { PageHeaderComponent, InlineAlertComponent } from '@shared/ui/components';
import { OrderService } from '../../services/order.service'; // Assuming you have this
import { ParcelDto, RepackCreateRequestDto, SenderInfoDto, ReceiverInfoDto, PreCarrierInfoDto, NextCarrierInfoDto, ParcelSenderInfoDto, ParcelReceiverInfoDto } from '../../../models/order.dto'; // Ensure DTO is imported
import { HttpErrorResponse } from '@angular/common/http';
import { RepackingStateService, PendingRepackState } from '../../services/repacking-state.service';


interface CartonForm {
  carton_no: string;
  carton_weight: string;
  carton_length: string;
  carton_width: string;
  carton_height: string;
  carton_volume: string;
  destination_region: string;
  goods_type: number;
  transport_type: number;
  service_code: string;
}

@Component({
  selector: 'app-repack-create',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ...HlmCardImports,
    HlmButton,
    NgIcon,
    HlmIcon,
    PageHeaderComponent,
    InlineAlertComponent,
  ],
  providers: [provideIcons({ lucideArrowLeft, lucideClipboard, lucideCheck, lucideAlertTriangle })],
  templateUrl: './repack-create.component.html',
  styleUrls: ['./repack-create.component.css']
})
export class RepackCreateComponent implements OnInit {
  parcelsToRepack: ParcelDto[] = [];
  originalOrderId: string = '';
  originalShopeeOrderNo: string = '';
  isLoading: boolean = false;
  errorMessage: string | null = null;
  successMessage: string | null = null;
  pendingRepackState: PendingRepackState | null = null;
  
  // Original carton details for reference
  originalCarton: {
    carton_weight?: number | null;
    carton_length?: number | null;
    carton_width?: number | null;
    carton_height?: number | null;
    carton_volume?: number | null;
  } = {};
  
  // Form model for the new carton
  newCarton: CartonForm = {
    carton_no: '',
    carton_weight: '',
    carton_length: '',
    carton_width: '',
    carton_height: '',
    carton_volume: '',
    destination_region: '',
    goods_type: 0,
    transport_type: 0,
    service_code: '',
  };

  // Properties for the new repack form (to be added later)
  //newCartonIdentifier: string = ''; 

  constructor(
    private router: Router,
    private orderService: OrderService,
     private repackingStateService: RepackingStateService
  ) {}  ngOnInit(): void {
    const pendingState = this.repackingStateService.getPendingRepackState();
    
    if (pendingState) {
        console.log("DEBUG: Pending state found:", JSON.stringify(pendingState));
        
        // Store the entire pending state
        this.pendingRepackState = pendingState;

        // Load parcels and reference IDs
        this.parcelsToRepack = pendingState.parcelsForRepack;
        this.originalOrderId = pendingState.sourceOrderUniqueId;
        this.originalShopeeOrderNo = pendingState.cancelledCartonIlhShopeeNo; // This is the Shopee No of the *cancelled* carton        // SIMPLIFIED WORKFLOW: Pre-fill only non-physical attributes
        if (pendingState.cartonConfigToReuse) {
            const config = pendingState.cartonConfigToReuse;
            this.newCarton.destination_region = config.destinationRegion || '';
            
            // Store original carton physical details for reference, but don't prefill form
            this.originalCarton = {
              carton_weight: config.cartonWeight,
              carton_length: config.cartonLength,
              carton_width: config.cartonWidth,
              carton_height: config.cartonHeight,
              carton_volume: config.cartonLength && config.cartonWidth && config.cartonHeight ? 
                             config.cartonLength * config.cartonWidth * config.cartonHeight : null
            };
            
            // Don't prefill physical dimensions - require user input
            // Physical dimensions must be input by the user:
            // this.newCarton.carton_weight
            // this.newCarton.carton_length
            // this.newCarton.carton_width
            // this.newCarton.carton_height
            this.newCarton.goods_type = config.goodsType ?? 0;
            this.newCarton.transport_type = config.transportType ?? 1;
            this.newCarton.service_code = config.serviceCode || '';
        }
    } else {
        console.log("DEBUG: No pending state found, checking router state...");
        
        // Fallback to router state for direct navigation (if supported)
        const navigation = this.router.getCurrentNavigation();
        const state = navigation?.extras?.state as any;
        
        if (state && state.parcelsToRepack && state.parcelsToRepack.length > 0) {            this.parcelsToRepack = state.parcelsToRepack;
            this.originalOrderId = state.originalOrderId || '';
            this.originalShopeeOrderNo = state.originalShopeeOrderNo || '';
          } else {
            this.errorMessage = "No parcels provided for repacking. Please cancel and try again.";
          }
        }
      
        // Final validation and debug logs
        if (this.parcelsToRepack.length === 0) {
          this.errorMessage = "No parcels provided to repack. Please go back and select parcels.";
        }
        
        if (!this.newCarton.destination_region && this.parcelsToRepack.length > 0) {
          this.errorMessage = (this.errorMessage || "") + " Destination region for the new carton is missing.";
        }

        console.log("DEBUG: Final form data after initialization:", JSON.stringify(this.newCarton, null, 2));
  }
  calculateVolume(): void {
    const length = parseFloat(this.newCarton.carton_length || '0');
    const width = parseFloat(this.newCarton.carton_width || '0');
    const height = parseFloat(this.newCarton.carton_height || '0');
    
    if (length > 0 && width > 0 && height > 0) {
      const volume = length * width * height;
      this.newCarton.carton_volume = volume.toFixed(2);
    } else {
      this.newCarton.carton_volume = '';
    }
  }
  onSubmitNewRepack(): void {
    this.isLoading = true;
    this.errorMessage = null;
    this.successMessage = null;
    
    // Basic validation
    if (this.parcelsToRepack.length === 0) { 
      this.errorMessage = "Cannot create repack: No parcels selected."; 
      this.isLoading = false; 
      return; 
    }
    
    if (!this.newCarton.carton_no || this.newCarton.carton_no.trim() === '') { 
      this.errorMessage = "New Carton Number is required."; 
      this.isLoading = false; 
      return; 
    }
    
    // Validate physical dimensions
    if (!this.newCarton.carton_weight || parseFloat(this.newCarton.carton_weight) <= 0) {
      this.errorMessage = "Valid carton weight is required.";
      this.isLoading = false;
      return;
    }
    
    if (!this.newCarton.carton_length || parseFloat(this.newCarton.carton_length) <= 0) {
      this.errorMessage = "Valid carton length is required.";
      this.isLoading = false;
      return;
    }
    
    if (!this.newCarton.carton_width || parseFloat(this.newCarton.carton_width) <= 0) {
      this.errorMessage = "Valid carton width is required.";
      this.isLoading = false;
      return;
    }
    
    if (!this.newCarton.carton_height || parseFloat(this.newCarton.carton_height) <= 0) {
      this.errorMessage = "Valid carton height is required.";
      this.isLoading = false;
      return;
    }
 
    // Ensure volume is calculated
    this.calculateVolume();

    // Extract tracking numbers from parcels
    const parcelTrackingNumbers = this.parcelsToRepack.map(p => p.trackingNumber).filter(tn => tn !== null) as string[];

    try {      // Check that all required fields are present before constructing request
      if (!this.pendingRepackState?.senderToUse) {
        this.errorMessage = "Sender information is missing";
        this.isLoading = false;
        return;
      }
      if (!this.pendingRepackState?.receiverToUse) {
        this.errorMessage = "Receiver information is missing";
        this.isLoading = false;
        return;
      }
      if (!this.pendingRepackState?.preCarrierToUse) {
        this.errorMessage = "Pre-carrier information is missing";
        this.isLoading = false;
        return;
      }
      if (!this.pendingRepackState?.nextCarrierToUse) {
        this.errorMessage = "Next-carrier information is missing";
        this.isLoading = false;
        return;
      }      // Make sure sender and receiver have required fields in the expected format
      const sender = this.ensureSenderFormat(this.pendingRepackState?.senderToUse);
      const receiver = this.ensureReceiverFormat(this.pendingRepackState?.receiverToUse);
      const preCarrierInfo = this.ensureCarrierFormat(this.pendingRepackState?.preCarrierToUse, 'pre');
      const nextCarrierInfo = this.ensureCarrierFormat(this.pendingRepackState?.nextCarrierToUse, 'next');

      const requestDto: RepackCreateRequestDto = {
        carton_no: this.newCarton.carton_no.trim(),
        carton_weight: parseFloat(this.newCarton.carton_weight || '0'),
        carton_length: parseFloat(this.newCarton.carton_length || '0'),
        carton_width: parseFloat(this.newCarton.carton_width || '0'),
        carton_height: parseFloat(this.newCarton.carton_height || '0'),
        carton_volume: parseFloat(this.newCarton.carton_volume || '0'),
        destination_region: this.newCarton.destination_region,
        parcel_list: parcelTrackingNumbers,
        goods_type: this.newCarton.goods_type,
        service_code: this.newCarton.service_code || '',
        transport_type: this.newCarton.transport_type,

        // Add sender and receiver information with correct field format
        sender: sender,
        receiver: receiver,
        
        // Add carrier information with correct field format
        preCarrierInfo: preCarrierInfo,
        nextCarrierInfo: nextCarrierInfo,

        // references to original order & shopee order number
        original_order_id: this.originalOrderId,
        original_shopee_order_no: this.originalShopeeOrderNo
      };

      console.log('DEBUG: Sending Repack Create Request:', JSON.stringify(requestDto, null, 2));

      // Add additional logging to check carrier info      console.log('DEBUG: Sender info:', JSON.stringify(this.pendingRepackState?.senderToUse, null, 2));
      console.log('DEBUG: Receiver info:', JSON.stringify(this.pendingRepackState?.receiverToUse, null, 2));
      console.log('DEBUG: Pre-carrier info:', JSON.stringify(this.pendingRepackState?.preCarrierToUse, null, 2));
      console.log('DEBUG: Next-carrier info:', JSON.stringify(this.pendingRepackState?.nextCarrierToUse, null, 2));
        this.orderService.repackCreate(requestDto).subscribe({
        next: (response: any) => {
          this.isLoading = false;
          // Clear any existing error message
          this.errorMessage = null;
          
          if (response?.success) {
            this.successMessage = `Updated carton ${response?.data?.carton_no || requestDto.carton_no} successfully. ILH Shopee No: ${response?.data?.ilh_shopee_no || 'N/A'}`;
            
            // Clear the pending state after successful creation
            this.repackingStateService.clearPendingRepackState();
            
            // Add a delay before navigating back to orders
            setTimeout(() => {
              this.router.navigate(['/dashboard/orders']);
            }, 2000);
          } else if (response?.message) {
            // If there's a message but success is false, still treat it as a success
            // if the backend is returning a success message with success:false
            if (response.message.includes('success') || 
                response.message.toLowerCase().includes('updated') || 
                response.message.toLowerCase().includes('created')) {
              this.successMessage = response.message;
              
              // Clear the pending state after successful creation
              this.repackingStateService.clearPendingRepackState();
              
              // Add a delay before navigating back to orders
              setTimeout(() => {
                this.router.navigate(['/dashboard/orders']);
              }, 2000);
            } else {
              // Only treat it as an error if the message doesn't indicate success
              this.errorMessage = response.message;
              console.error('API error response:', response);
            }
          } else {
            // Handle success:false responses with no message
            this.errorMessage = 'Repack create operation failed';
            console.error('API error response:', response);
          }
        },        error: (error: HttpErrorResponse) => {
          this.isLoading = false;
          console.error('Error creating repack:', error);
          
          // Clear any existing success message
          this.successMessage = null;
          
          // Check if the error message actually indicates success
          // Some backend endpoints might return error status codes with success messages
          if (error.error?.message && (
              error.error.message.includes('success') || 
              error.error.message.toLowerCase().includes('updated') || 
              error.error.message.toLowerCase().includes('created') ||
              error.error.message.includes('Order successfully repacked'))) {
            
            this.successMessage = error.error.message;
            this.errorMessage = null;
            
            // Clear the pending state after successful creation
            this.repackingStateService.clearPendingRepackState();
            
            // Add a delay before navigating back to orders
            setTimeout(() => {
              this.router.navigate(['/dashboard/orders']);
            }, 2000);
            
            return;
          }
          
          // Clear any existing success message
          this.successMessage = null;
          
          // Check if the error message actually indicates success
          // Some backend endpoints might return error status codes with success messages
          if (error.error?.message && (
              error.error.message.includes('success') || 
              error.error.message.toLowerCase().includes('updated') || 
              error.error.message.toLowerCase().includes('created'))) {
            
            this.successMessage = error.error.message;
            
            // Clear the pending state after successful creation
            this.repackingStateService.clearPendingRepackState();
            
            // Add a delay before navigating back to orders
            setTimeout(() => {
              this.router.navigate(['/dashboard/orders']);
            }, 2000);
            
            return;
          }
          
          // Extract the error message from the response if possible
          if (error.error?.message) {
            this.errorMessage = `Error: ${error.error.message}`;
          } else if (error.status === 400) {
            this.errorMessage = 'Invalid request data. Please check all fields and try again.';
          } else if (error.status === 404) {
            this.errorMessage = 'The server endpoint was not found. Please contact support.';
          } else if (error.status === 0) {
            this.errorMessage = 'Cannot connect to the server. Please check your connection.';
          } else {
            this.errorMessage = `Error creating repack: ${error.status} ${error.statusText}`;
          }
        }      });
    } catch (e: any) {
      this.isLoading = false;
      
      // Check if the error message actually indicates success
      if (e.message && (
          e.message.includes('success') || 
          e.message.includes('Order successfully repacked') ||
          e.message.toLowerCase().includes('updated') || 
          e.message.toLowerCase().includes('created'))) {
        
        this.successMessage = e.message;
        this.errorMessage = null;
        
        // Clear the pending state after successful creation
        this.repackingStateService.clearPendingRepackState();
        
        // Add a delay before navigating back to orders
        setTimeout(() => {
          this.router.navigate(['/dashboard/orders']);
        }, 2000);
        
        return;
      }
      
      this.errorMessage = `Error preparing repack request: ${e.message}. Please check all required fields.`;
      console.error("Error preparing repack request:", e);
    }
  }

  goBack(): void {
    this.router.navigate(['/dashboard/orders']);
  }

  // Method to copy original carton dimensions to the new carton
  copyOriginalDimensions(): void {
    if (this.originalCarton) {
      if (this.originalCarton.carton_weight) {
        this.newCarton.carton_weight = this.originalCarton.carton_weight.toString();
      }
      if (this.originalCarton.carton_length) {
        this.newCarton.carton_length = this.originalCarton.carton_length.toString();
      }
      if (this.originalCarton.carton_width) {
        this.newCarton.carton_width = this.originalCarton.carton_width.toString();
      }
      if (this.originalCarton.carton_height) {
        this.newCarton.carton_height = this.originalCarton.carton_height.toString();
      }
      
      // Calculate volume after setting dimensions
      this.calculateVolume();
    }
  }

  // Helper mapping methods
  private mapParcelSenderToForm(sender: any): any {
    return {
      name: sender.name || '',
      phone: String(sender.phone || 0),
      postalCode: String(sender.postalCode || sender.zipcode || 0),
      region: sender.region || '',
      region_code: sender.region_code || '',
      state: sender.state || '',
      city: sender.city || '',
      address: sender.address || '',
      email: sender.email || '',
      district: sender.district || '',
      street: sender.street || ''
    };
  }

  private mapParcelReceiverToForm(receiver: any): any {
    return {
      name: receiver.name || '',
      phone: String(receiver.phone || 0),
      postalCode: String(receiver.postalCode || receiver.zipcode || 0),
      region: receiver.region || '',
      region_code: receiver.region_code || '',
      state: receiver.state || '',
      city: receiver.city || '',
      address: receiver.address || '',
      email: receiver.email || '',
      district: receiver.district || '',
      street: receiver.street || ''
    };
  }

  // Helper methods for ensuring correct payload format
  private ensureSenderFormat(sender: SenderInfoDto | undefined): SenderInfoDto {
    if (!sender) {
      return {
        name: '',
        phone: 0,
        zipcode: 0,
        region: '',
        region_code: '',
        state: '',
        city: '',
        address: ''
      };
    }

    // Make sure all fields are present in the expected format
    return {
      name: sender.name || '',
      phone: typeof sender.phone === 'number' ? sender.phone : Number(sender.phone) || 0,
      zipcode: typeof sender.zipcode === 'number' ? sender.zipcode : Number(sender.zipcode) || 0,
      region: sender.region || '',
      region_code: sender.region_code || '',
      state: sender.state || '',
      city: sender.city || '',
      address: sender.address || '',
      email: sender.email,
      district: sender.district,
      street: sender.street
    };
  }

  private ensureReceiverFormat(receiver: ReceiverInfoDto | undefined): ReceiverInfoDto {
    if (!receiver) {
      return {
        name: '',
        phone: 0,
        zipcode: 0,
        region: '',
        region_code: '',
        state: '',
        city: '',
        address: ''
      };
    }

    // Make sure all fields are present in the expected format
    return {
      name: receiver.name || '',
      phone: typeof receiver.phone === 'number' ? receiver.phone : Number(receiver.phone) || 0,
      zipcode: typeof receiver.zipcode === 'number' ? receiver.zipcode : Number(receiver.zipcode) || 0,
      region: receiver.region || '',
      region_code: receiver.region_code || '',
      state: receiver.state || '',
      city: receiver.city || '',
      address: receiver.address || '',
      email: receiver.email,
      district: receiver.district,
      street: receiver.street
    };
  }

  private ensureCarrierFormat(carrier: any, type: 'pre' | 'next'): any {
    if (!carrier) {
      return {
        carrier_code: 0,
        carrier_name: '',
        carrier_tn: '',
        carrier_ext_tn: ''
      };
    }

    // Handle different field names that might be in the source data
    return {
      carrier_code: typeof carrier.carrier_code === 'number' ? carrier.carrier_code : Number(carrier.carrier_code) || 0,
      carrier_name: carrier.carrier_name || '',
      carrier_tn: carrier.carrier_tn || '',
      carrier_ext_tn: carrier.carrier_ext_tn || ''
    };
  }
}



